import React, { useState, useEffect, useCallback, useMemo } from "react";
import { MDBSpinner, MDBTooltip } from "mdb-react-ui-kit";
import { ActionButton, DataTable, Tag, TableStatusDropdown } from "./ui";
import Toolbar from "./Toolbar";
import { useTranslation } from "react-i18next";
import ConfirmModal from "./Modals/ConfirmModal";
import { api, getTenantApiScope } from "../services";
import { toast } from "react-toastify";
import {
  RotateCcw,
} from "lucide-react";
import AddUserWizard from "./UserManagement/AddUserWizard";
import EditUserModal from "./UserManagement/EditUserModal";
import {
  normalizeRestaurantId,
  restaurantIdFromAuthToken,
  userIdFromAuthToken,
} from "../utils/utils";
import { parseRolesListResponse } from "../utils/rbacApi";
import { pickLatestAssignment } from "../utils/userScope";
import { usePermission } from "../hooks/usePermission";
import { RBAC_MODULES, PERMISSION_ACTIONS } from "../config/routePermissions";

const BRAND = "#083B4C";
const SEARCH_DEBOUNCE_MS = 350;
const FONT_UI = "'Inter', 'Noto Sans Arabic', 'Segoe UI', sans-serif";
const STAFF_STATUS_OPTIONS = ["Active", "Inactive"];

/** Maps DataTable column ids to GET /v1/staff-users sortBy fields */
const USER_TABLE_SORT_FIELDS = {
  name: "name",
  username: "username",
  createdAt: "createdAt",
  status: "status",
};

/** Single-line created-at for table cells. */
function formatCreatedAtOneLine(dateValue, localeHint) {
  const d = dateValue ? new Date(dateValue) : null;
  if (!d || Number.isNaN(d.getTime())) return null;
  const lng = localeHint || "en-US";
  try {
    return d.toLocaleString(lng, {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  } catch {
    return null;
  }
}

/** Normalize API/user status strings for UI + Mongoose enum values. */
function normalizeStaffStatus(status) {
  const s = String(status ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
  if (s === "active") return "Active";
  if (s === "inactive") return "Inactive";
  if (s === "under review") return "Under Review";
  return String(status ?? "").trim() || "Inactive";
}

function UserManagement() {
  const { t, i18n } = useTranslation("translation");
  const isRtl = i18n.dir() === "rtl";
  const { can, bypass, isStaff } = usePermission();
  const canInvite = can(RBAC_MODULES.USERS, "invite");
  const canCreateUser = can(RBAC_MODULES.USERS, PERMISSION_ACTIONS.ADD);
  const canEditUser = can(RBAC_MODULES.USERS, PERMISSION_ACTIONS.EDIT);
  const canDeleteUser = can(RBAC_MODULES.USERS, PERMISSION_ACTIONS.DELETE);
  const noPermissionTip = t("noPermissionToAccessThis");
  const tp = (key, opts) => t(`userManagementPage.${key}`, opts);
  const ownAccountTip = tp("cannotEditDeleteOwnAccount");
  const sessionUserId = useMemo(() => userIdFromAuthToken(), []);

  const isLoggedInUser = useCallback(
    (userRow) => {
      if (!sessionUserId) return false;
      const rowId = userRow?._id ?? userRow?.id;
      return rowId != null && String(rowId) === sessionUserId;
    },
    [sessionUserId]
  );

  const [restaurantId, setRestaurantId] = useState(null);
  const [tenantResolved, setTenantResolved] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [dateRangePreset, setDateRangePreset] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortField, setSortField] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");

  const [showWizard, setShowWizard] = useState(false);

  const [editUser, setEditUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmData, setConfirmData] = useState({ title: "", message: "", action: () => {}, actionData: null });

  const sessionToast = () => toast.error(tp("sessionNoRestaurant"));
  const locale = useMemo(() => (i18n.language?.startsWith("ar") ? "ar-SA" : undefined), [i18n.language]);

  const apiDateRange = useMemo(() => {
    if (!dateRangePreset || dateRangePreset === "all") {
      return { createdFrom: "", createdTo: "" };
    }
    const days = parseInt(dateRangePreset, 10);
    if (!Number.isFinite(days) || days < 1) {
      return { createdFrom: "", createdTo: "" };
    }
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    start.setHours(0, 0, 0, 0);
    const pad = (n) => String(n).padStart(2, "0");
    const toYmd = (dt) => `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
    return { createdFrom: toYmd(start), createdTo: toYmd(end) };
  }, [dateRangePreset]);

  const fetchUsers = useCallback(async () => {
    // Staff users need tenant resolution, company/admin users don't
    if (isStaff && !tenantResolved) return;

    setLoading(true);
    try {
      const params = new URLSearchParams();

      params.set("page", String(currentPage));
      params.set("limit", String(itemsPerPage));
      if (debouncedSearch.trim()) params.append("search", debouncedSearch.trim());
      if (selectedRole) params.append("roleId", selectedRole);
      if (selectedStatus) params.append("status", selectedStatus);
      const { createdFrom, createdTo } = apiDateRange;
      if (createdFrom) params.append("createdFrom", createdFrom);
      if (createdTo) params.append("createdTo", createdTo);
      params.set("sortBy", sortField);
      params.set("sortOrder", sortOrder);

      const res = await api.get(`/v1/staff-users?${params}`);
      const rows = res.data.data || [];
      setUsers(rows);
      const pg = res.data.pagination || {};
      const total = typeof pg.total === "number" ? pg.total : rows.length;
      const pagesFromTotal = total === 0 ? 1 : Math.max(1, Math.ceil(total / itemsPerPage));
      setTotalPages(pagesFromTotal);
      setTotalCount(total);
    } catch (err) {
      toast.error(err.response?.data?.message || tp("loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [isStaff, tenantResolved, currentPage, debouncedSearch, selectedRole, selectedStatus, apiDateRange, itemsPerPage, sortField, sortOrder]);

  const fetchRoles = useCallback(async () => {
    // Staff users need tenant resolution, company/admin users don't
    if (isStaff && !tenantResolved) {
      setRoles([]);
      return;
    }

    try {
      // Omit restaurant anchor for staff — backend resolves scope from assignments (see listRoles).
      const res = await api.get("/v1/roles");
      setRoles(parseRolesListResponse(res));
    } catch {
      setRoles([]);
    }
  }, [isStaff, tenantResolved]);

  useEffect(() => {
    // Company/admin users don't need tenant resolution, they can work without restaurantId
    if (bypass) {
      setTenantResolved(true);
      return;
    }

    // Staff users need tenant resolution
    const fromJwt = restaurantIdFromAuthToken();
    if (fromJwt) {
      setRestaurantId(fromJwt);
      setTenantResolved(true);
      return;
    }

    const raw = localStorage.getItem("user");
    if (raw) {
      try {
        const u = JSON.parse(raw);
        const fromUser = normalizeRestaurantId(u.restaurantId);
        if (fromUser) {
          setRestaurantId(fromUser);
          setTenantResolved(true);
          return;
        }
      } catch {
        /* fall through */
      }
    }

    const fromDashboardScope = normalizeRestaurantId(getTenantApiScope().storeId);
    if (fromDashboardScope) {
      setRestaurantId(fromDashboardScope);
      setTenantResolved(true);
      return;
    }

    api
      .get("/stores/my")
      .then((res) => {
        const firstStore = res.data?.stores?.[0];
        const id = normalizeRestaurantId(firstStore?._id);
        if (id) setRestaurantId(id);
      })
      .catch(() => {})
      .finally(() => setTenantResolved(true));
  }, [bypass]);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(searchQuery), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [searchQuery]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);
  useEffect(() => { fetchRoles(); }, [fetchRoles]);
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, selectedRole, selectedStatus, dateRangePreset, sortField, sortOrder]);

  const applyTableSort = (column, direction) => {
    const apiField = USER_TABLE_SORT_FIELDS[column];
    if (!apiField) return;
    setSortField(apiField);
    setSortOrder(direction);
    setCurrentPage(1);
  };

  /** Keep page in range when filters or totals shrink */
  useEffect(() => {
    setCurrentPage((p) => {
      if (totalPages < 1) return 1;
      return p > totalPages ? totalPages : p;
    });
  }, [totalPages]);

  const requestStatusChange = (user, newStatus) => {
    if (!canEditUser) {
      toast.error(noPermissionTip);
      return;
    }
    if (isLoggedInUser(user)) {
      return;
    }
    if (!newStatus || newStatus === normalizeStaffStatus(user.status)) return;
    if (newStatus === "Under Review") return;
    const activating = newStatus === "Active";
    setConfirmData({
      title: activating ? tp("activateUser") : tp("deactivateUser"),
      message: activating
        ? tp("confirmActivate", { username: user.username })
        : tp("confirmDeactivate", { username: user.username }),
      action: async () => {
        try {
          const payload = { status: newStatus };
          
          // Include restaurantId if we have one (for staff users or when needed)
          const rid =
            normalizeRestaurantId(restaurantId) ??
            normalizeRestaurantId(getTenantApiScope().storeId);
          if (rid) {
            payload.restaurantId = rid;
          } else if (isStaff) {
            sessionToast();
            return;
          }
          
          await api.patch(`/v1/staff-users/${user._id}/status`, payload);
          toast.success(activating ? tp("userActivated") : tp("userDeactivated"));
          fetchUsers();
        } catch (err) {
          toast.error(err.response?.data?.message || tp("statusUpdateFailed"));
        }
      },
      actionData: null,
    });
    setShowConfirm(true);
  };

  const handleDelete = (user) => {
    if (!canDeleteUser) {
      toast.error(noPermissionTip);
      return;
    }
    if (isLoggedInUser(user)) {
      return;
    }
    setConfirmData({
      title: tp("deleteUser"),
      message: tp("deleteConfirm", { username: user.username }),
      action: async () => {
        try {
          const rid =
            normalizeRestaurantId(restaurantId) ??
            normalizeRestaurantId(getTenantApiScope().storeId);
          
          let url = `/v1/staff-users/${user._id}`;
          if (rid) {
            url += `?restaurantId=${encodeURIComponent(rid)}`;
          } else if (isStaff) {
            sessionToast();
            return;
          }
          
          await api.delete(url);
          toast.success(tp("userDeleted"));
          fetchUsers();
        } catch (err) {
          toast.error(err.response?.data?.message || tp("deleteFailed"));
        }
      },
      actionData: null,
    });
    setShowConfirm(true);
  };

  const handleResendInvite = async (user) => {
    if (!canInvite) {
      toast.error(noPermissionTip);
      return;
    }
    try {
      const payload = {};
      
      // Include restaurantId if we have one (for staff users or when needed)
      const rid =
        normalizeRestaurantId(restaurantId) ??
        normalizeRestaurantId(getTenantApiScope().storeId);
      if (rid) {
        payload.restaurantId = rid;
      } else if (isStaff) {
        sessionToast();
        return;
      }
      
      await api.post(`/v1/staff-users/${user._id}/resend-invite`, payload);
      toast.success(tp("inviteResent"));
    } catch (err) {
      toast.error(err.response?.data?.message || tp("resendInviteFailed"));
    }
  };

  const handleEditOpen = async (user) => {
    if (!canEditUser) {
      toast.error(noPermissionTip);
      return;
    }
    if (isLoggedInUser(user)) {
      return;
    }
    const id = user._id ?? user.id;
    if (!id) return;
    try {
      const res = await api.get(`/v1/staff-users/${id}`);
      setEditUser(res.data);
    } catch {
      setEditUser({ ...user });
    }
    setShowEditModal(true);
  };

  const displayNameForUser = useCallback(
    (userRow) => {
      const fn = String(userRow?.firstName ?? "").trim();
      const ln = String(userRow?.lastName ?? "").trim();
      const full = `${fn} ${ln}`.trim();
      if (full) return full;

      const raw = String(userRow?.username || "").trim();
      if (!raw) return tp("emptyValue");
      const spaced = raw.includes("_") ? raw.replace(/_/g, " ") : raw;
      if (/[\u0600-\u06FF]/.test(spaced)) {
        return spaced;
      }
      if (/\s/.test(spaced)) {
        return spaced
          .split(/\s+/)
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
          .join(" ");
      }
      return spaced;
    },
    [tp]
  );

  const rolePills = (user) => {
    const list = user.assignments || [];
    const latest = pickLatestAssignment(list);
    const name = latest?.roleId?.name;
    if (!name) {
      return <span className="um-muted">{tp("noRoleAssigned")}</span>;
    }
    return (
      <div className="um-role-stack">
        <Tag key={latest._id || `${user._id}-role-latest`} variant="role">
          {name}
        </Tag>
      </div>
    );
  };

  const statusLabel = (status) => {
    const st = normalizeStaffStatus(status);
    if (st === "Active") return tp("statusActiveLabel");
    if (st === "Inactive") return tp("statusInactiveLabel");
    if (st === "Under Review") return tp("statusUnderReviewLabel");
    if (st === "PendingVerification") return t("pendingverification");
    return st;
  };

  const statusTagVariant = (status) => {
    const st = normalizeStaffStatus(status);
    if (st === "Active") return "success";
    if (st === "Inactive") return "neutral";
    return "warning";
  };

  const statusControl = (user) => {
    const isSelf = isLoggedInUser(user);
    const canChange = canEditUser && !isSelf;

    return (
      <TableStatusDropdown
        value={user.status}
        options={STAFF_STATUS_OPTIONS}
        getLabel={statusLabel}
        getVariant={statusTagVariant}
        normalizeValue={normalizeStaffStatus}
        onChange={(newStatus) => requestStatusChange(user, newStatus)}
        disabled={!canChange}
        showChevron={canEditUser && (canChange || isSelf)}
        disabledTooltip={isSelf ? ownAccountTip : undefined}
        ariaLabel={`${tp("openStatusMenu")}: ${displayNameForUser(user)}`}
      />
    );
  };

  const roleDropdownOptions = roles.map((r) => ({ _id: r._id, name: r.name }));
  const statusFilterOptions = useMemo(
    () => [
      { id: "", name: tp("allStatuses") },
      { id: "Active", name: tp("statusActiveLabel") },
      { id: "Inactive", name: tp("statusInactiveLabel") },
    ],
    [tp]
  );
  const dateRangeOptions = useMemo(
    () => [
      { _id: "7", name: tp("dateRangeLast7") },
      { _id: "30", name: tp("dateRangeLast30") },
      { _id: "90", name: tp("dateRangeLast90") },
    ],
    [tp]
  );

  const tenantId = bypass 
    ? (normalizeRestaurantId(restaurantId) ?? normalizeRestaurantId(getTenantApiScope().storeId))
    : (normalizeRestaurantId(restaurantId) ?? normalizeRestaurantId(getTenantApiScope().storeId));

  const userColumns = useMemo(
    () => [
      {
        id: "name",
        header: t("Name"),
        sortable: true,
        minWidth: 160,
        className: "oh-table-td--strong",
        render: (user) => (
          <div className="um-user-name">{displayNameForUser(user)}</div>
        ),
      },
      {
        id: "username",
        header: tp("columnUsername"),
        sortable: true,
        minWidth: 120,
        render: (user) => user.username || tp("emptyValue"),
        className: "um-td-username",
      },
      {
        id: "role",
        header: t("Role"),
        minWidth: 140,
        render: (user) => rolePills(user),
      },
      {
        id: "createdAt",
        header: tp("columnCreatedAt"),
        sortable: true,
        minWidth: 180,
        className: "um-td-created",
        render: (user) => {
          const createdAtLine = formatCreatedAtOneLine(user.createdAt, locale);
          return (
            <span className="um-created-at-line">
              {createdAtLine ?? tp("emptyValue")}
            </span>
          );
        },
      },
      {
        id: "status",
        header: t("Status"),
        sortable: true,
        minWidth: 132,
        render: (user) => statusControl(user),
      },
      {
        id: "actions",
        header: tp("columnAction"),
        minWidth: 300,
        headerClassName: "oh-table-th--actions",
        className: "oh-table-td--actions",
        render: (user) => {
          const isSelf = isLoggedInUser(user);
          return (
            <div className="um-action-buttons oh-table-actions">
              {canEditUser && !isSelf ? (
                <ActionButton
                  variant="edit"
                  onClick={() => handleEditOpen(user)}
                  aria-label={`${tp("editUppercase")} ${displayNameForUser(user)}`}
                >
                  {tp("editUppercase")}
                </ActionButton>
              ) : canEditUser && isSelf ? (
                <MDBTooltip tag="span" title={ownAccountTip}>
                  <span className="d-inline-block">
                    <ActionButton
                      variant="edit"
                      disabled
                      aria-label={`${tp("editUppercase")} ${displayNameForUser(user)}`}
                    >
                      {tp("editUppercase")}
                    </ActionButton>
                  </span>
                </MDBTooltip>
              ) : (
                <MDBTooltip tag="span" title={noPermissionTip}>
                  <span className="d-inline-block">
                    <ActionButton
                      variant="edit"
                      disabled
                      onClick={() => handleEditOpen(user)}
                      aria-label={`${tp("editUppercase")} ${displayNameForUser(user)}`}
                    >
                      {tp("editUppercase")}
                    </ActionButton>
                  </span>
                </MDBTooltip>
              )}
              {user.invitePending && canInvite ? (
                <ActionButton
                  variant="refresh"
                  iconOnly
                  icon={<RotateCcw size={13} strokeWidth={1.5} aria-hidden="true" />}
                  onClick={() => handleResendInvite(user)}
                  title={tp("resendInviteTitle")}
                  aria-label={`${tp("resendInviteTitle")} ${displayNameForUser(user)}`}
                />
              ) : user.invitePending && !canInvite ? (
                <MDBTooltip tag="span" title={noPermissionTip}>
                  <span className="d-inline-block">
                    <ActionButton
                      variant="refresh"
                      iconOnly
                      icon={<RotateCcw size={13} strokeWidth={1.5} aria-hidden="true" />}
                      disabled
                      onClick={() => handleResendInvite(user)}
                      title={tp("resendInviteTitle")}
                      aria-label={`${tp("resendInviteTitle")} ${displayNameForUser(user)}`}
                    />
                  </span>
                </MDBTooltip>
              ) : null}
              {canDeleteUser && !isSelf ? (
                <ActionButton
                  variant="delete"
                  onClick={() => handleDelete(user)}
                  aria-label={`${tp("deleteUppercase")} ${displayNameForUser(user)}`}
                >
                  {tp("deleteUppercase")}
                </ActionButton>
              ) : canDeleteUser && isSelf ? (
                <MDBTooltip tag="span" title={ownAccountTip}>
                  <span className="d-inline-block">
                    <ActionButton
                      variant="delete"
                      disabled
                      aria-label={`${tp("deleteUppercase")} ${displayNameForUser(user)}`}
                    >
                      {tp("deleteUppercase")}
                    </ActionButton>
                  </span>
                </MDBTooltip>
              ) : (
                <MDBTooltip tag="span" title={noPermissionTip}>
                  <span className="d-inline-block">
                    <ActionButton
                      variant="delete"
                      disabled
                      onClick={() => handleDelete(user)}
                      aria-label={`${tp("deleteUppercase")} ${displayNameForUser(user)}`}
                    >
                      {tp("deleteUppercase")}
                    </ActionButton>
                  </span>
                </MDBTooltip>
              )}
            </div>
          );
        },
      },
    ],
    [
      t,
      tp,
      locale,
      canEditUser,
      canDeleteUser,
      canInvite,
      noPermissionTip,
      ownAccountTip,
      displayNameForUser,
      isLoggedInUser,
    ]
  );

  const handleAddUserClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!canCreateUser) {
      toast.error(noPermissionTip);
      return;
    }

    // Only check tenantId for staff users, not for company/admin users
    if (isStaff && !tenantId) {
      sessionToast();
      return;
    }

    setShowWizard(true);
  };

  return (
    <div
      dir={i18n.dir()}
      lang={i18n.language}
      className="user-management-clean oh-page"
      style={{
        backgroundColor: "#EEEEEE",
        minHeight: "100vh",
        fontFamily: FONT_UI,
      }}
    >
      <style>{cleanTableStyles}</style>
        <div className="oh-page-header um-header">
          <h1 className="page-title-header">{tp("pageTitle")}</h1>
        </div>

        <div className="oh-page-section">
          <Toolbar
            hideIcons={true}
            toolbarVariant="user-management"
            showSearch={true}
            searchQuery={searchQuery}
            handleSearch={(e) => setSearchQuery(e.target.value)}
            searchPlaceHolder={tp("searchPlaceholderPlain")}
            extraDropdowns={[
              {
                label: tp("dateRangeLabel"),
                placeholder: tp("dateRangeAll"),
                options: dateRangeOptions,
                selectedOption: dateRangePreset === "all" ? null : dateRangePreset,
                handleSelect: (opt) => setDateRangePreset(String(opt?._id || "all")),
                showClear: true,
              },
              {
                type: "filter",
                label: t("Status"),
                orderStatuses: statusFilterOptions,
                activeTab: selectedStatus || "",
                setActiveTab: setSelectedStatus,
              },
              {
                label: t("Role"),
                placeholder: tp("allRoles"),
                options: roleDropdownOptions,
                selectedOption: selectedRole || null,
                handleSelect: (opt) => setSelectedRole(opt?._id ?? ""),
              },
            ]}
          />
        </div>

        {!tenantResolved ? (
          <div className="um-loading-state">
            <MDBSpinner color="primary" />
          </div>
        ) : !tenantId && isStaff ? (
          <div className="um-warning-state">
            <div className="um-warning-content">
              <strong className="um-warning-title">{tp("accountAccessTitle")}</strong>
              <span className="um-warning-message">{tp("accountAccessBody")}</span>
            </div>
          </div>
        ) : (
          <div className="oh-page-section">
          <DataTable
            tableId="user-management"
            title={t("Users")}
            columns={userColumns}
            data={users}
            rowKey="_id"
            loading={loading}
            isRtl={isRtl}
            sort={{
              column:
                Object.entries(USER_TABLE_SORT_FIELDS).find(([, v]) => v === sortField)?.[0] ||
                sortField,
              direction: sortOrder,
              onChange: ({ column, direction }) => applyTableSort(column, direction),
            }}
            primaryAction={
              canCreateUser
                ? {
                    label: t("Add User"),
                    onClick: handleAddUserClick,
                  }
                : {
                    label: t("Add User"),
                    onClick: handleAddUserClick,
                    disabled: true,
                    title: noPermissionTip,
                  }
            }
            emptyState={
              <div className="um-empty-content">
                <h3>{tp("noUsers")}</h3>
                <p>{tp("noUsersMatchingCriteria")}</p>
              </div>
            }
            pagination={{
              page: currentPage,
              pageSize: itemsPerPage,
              total: totalCount,
              onPageChange: setCurrentPage,
              onPageSizeChange: (n) => {
                setItemsPerPage(n);
                setCurrentPage(1);
              },
              pageSizeOptions: [10, 25, 50],
              showLabel: tp("show"),
              ofLabel: t("of", { defaultValue: "of" }),
              resultsLabel: t("results", { defaultValue: "results" }),
              isRtl,
              previousLabel: t("previous"),
              nextLabel: t("next"),
            }}
          />
          </div>
        )}

      <AddUserWizard 
        show={showWizard} 
        onHide={() => setShowWizard(false)} 
        restaurantId={tenantId} 
        onCreated={fetchUsers} 
      />

      <EditUserModal
        show={showEditModal}
        user={editUser}
        restaurantId={tenantId}
        onHide={() => setShowEditModal(false)}
        onSaved={fetchUsers}
        onRequestDelete={(u) => {
          setShowEditModal(false);
          handleDelete(u);
        }}
      />

      <ConfirmModal
        show={showConfirm}
        onHide={() => setShowConfirm(false)}
        title={confirmData.title}
        message={confirmData.message}
        action={confirmData.action}
        actionData={confirmData.actionData}
        visibility={null}
      />
    </div>
  );
}

export default UserManagement;

const cleanTableStyles = `
.user-management-clean {
  font-family: ${FONT_UI};
  width: 100%;
}

.um-loading-state {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px 20px;
  background: #FFFFFF;
  border-radius: 12px;
  border: 1px solid #E5E7EB;
}

.um-warning-state {
  background: #FFFFFF;
  border: 1px solid #F59E0B;
  border-radius: 12px;
  padding: 24px;
  margin: 20px 0;
}

.um-warning-content {
  max-width: 600px;
}

.um-warning-title {
  display: block;
  font-weight: 600;
  color: #92400E;
  margin-bottom: 8px;
}

.um-warning-message {
  color: #78350F;
  font-size: 14px;
}

.um-user-name {
  font-family: Inter, sans-serif;
  font-weight: 600;
  font-size: 12px;
  line-height: 150%;
  color: #030F1B;
}

.um-td-created {
  vertical-align: top;
}

.um-created-at-line {
  display: block;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  font-size: 12px;
  color: var(--oh-table-cell-text, #030f1b);
  font-variant-numeric: tabular-nums;
  line-height: 1.5;
}

.um-td-username {
  color: #6B7280;
  font-size: 14px;
}

.um-role-stack {
  display: flex;
  align-items: center;
  gap: 6px;
}

.um-role-pill-ref {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  background: #E6F6F9;
  color: #118AB2;
  border: 1px solid #118AB2;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.um-role-pill-ref:hover {
  background: #D1F0F6;
  border-color: #0F7A94;
}

.um-muted {
  font-size: 13px;
  color: #9CA3AF;
  font-style: italic;
}

.um-action-buttons {
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  flex-wrap: wrap;
}

.um-empty-content h3 {
  font-size: 18px;
  font-weight: 600;
  color: #6B7280;
  margin: 0 0 8px 0;
}

.um-empty-content p {
  font-size: 14px;
  color: #9CA3AF;
  margin: 0;
}

@media (max-width: 768px) {
  .um-user-name {
    font-size: 12px;
  }
}
`;
